import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import AdminLayout from '@/components/layout/AdminLayout'

export default function BulkImportPage() {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const features = [
    'Upload CSV or Excel files with item data',
    'Preview and validate data before import',
    'Bulk assign NFC tags to imported items', 
    'Track import progress and handle errors'
  ]

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    setFile(selectedFile || null)
  }

  const handleUpload = async () => {
    if (!file) return
    
    setUploading(true)
    
    // Simulate upload
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    alert('Upload functionality will be implemented soon!')
    setUploading(false)
  }

  const downloadTemplate = () => {
    // Create a sample CSV template
    const headers = [
      'player_name',
      'jersey_number', 
      'team',
      'size',
      'condition',
      'game_title',
      'game_date',
      'opponent',
      'venue',
      'touchdowns',
      'passing_yards',
      'completions',
      'description'
    ]
    
    const sampleRow = [
      'Tom Brady',
      '12',
      'patriots',
      'l',
      'new',
      'Super Bowl LI',
      '2017-02-05',
      'Atlanta Falcons',
      'NRG Stadium, Houston',
      '2',
      '466',
      '43',
      'Historic comeback victory in Super Bowl LI'
    ]

    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = 'jersey_import_template.csv'
    a.click()
    
    window.URL.revokeObjectURL(url)
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Hero Section */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">Bulk Import</h1>
              <p className="text-lg text-gray-600">Import multiple items from CSV or Excel files</p>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Upload File</h2>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {file ? file.name : 'Choose file to upload'}
                </p>
                <p className="text-gray-600">or drag and drop</p>
                <p className="text-sm text-gray-500 mt-2">CSV, XLSX files up to 10MB</p>
              </label>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {uploading ? 'Uploading...' : 'Upload & Process'}
              </Button>
              
              <Button
                onClick={downloadTemplate}
                variant="outline"
              >
                Download Template
              </Button>
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Coming Soon</h2>
            
            <p className="text-gray-700 mb-6">This feature will allow you to:</p>

            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700">{feature}</p>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-1">Development Note</p>
              <p className="text-sm text-blue-700">
                TODO: Implement using libraries like Papa Parse for CSV processing and file upload handlers with progress tracking.
              </p>
            </div>
          </div>

          {/* Sample Data Preview */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Expected Data Format</h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-900">Player Name</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-900">Jersey #</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-900">Team</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-900">Size</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-900">Condition</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-900">Game</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 px-3 text-gray-900">Tom Brady</td>
                    <td className="py-2 px-3 text-gray-700">12</td>
                    <td className="py-2 px-3 text-gray-700">patriots</td>
                    <td className="py-2 px-3 text-gray-700">L</td>
                    <td className="py-2 px-3 text-gray-700">new</td>
                    <td className="py-2 px-3 text-gray-700">Super Bowl LI</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-2 px-3 text-gray-900">Patrick Mahomes</td>
                    <td className="py-2 px-3 text-gray-700">15</td>
                    <td className="py-2 px-3 text-gray-700">chiefs</td>
                    <td className="py-2 px-3 text-gray-700">L</td>
                    <td className="py-2 px-3 text-gray-700">like-new</td>
                    <td className="py-2 px-3 text-gray-700">Super Bowl LIV</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <p className="text-sm text-gray-600 mt-4">
              Download the template above for the complete format including optional fields like stats and description.
            </p>
          </div>

          {/* Action Button */}
          <div className="text-center">
            <Button 
              onClick={() => navigate('/admin/dashboard')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}